import React from 'react';
import { createBeautifulMentionNode } from 'lexical-beautiful-mentions';

/**
 * The rendered mention pill for lexical-beautiful-mentions.
 *
 * Styling mirrors the DISPLAY-side mention pill (richText.js splitMentions /
 * DEFAULT_MENTION_STYLE) so the composer and the message list look identical:
 * primary accent text on a translucent accent wash.
 *
 * The library passes `trigger` ('@'), `value` (the username) and `data`
 * (whatever the onSearch item carried — here `{ id, usernameColor, ... }`). We
 * render "@username" in the mentioned user's OWN color (data.usernameColor,
 * already resolved to chosen-or-role-default in MentionPlugin's onSearch) so the
 * composer pill matches the sent message. The userId rides along in `data.id`
 * and is read back out by the serializer (serialize.js) to emit `@[value](id)`.
 *
 * `React.forwardRef` is REQUIRED: the library attaches a ref to the mention
 * element for selection/positioning. It also spreads extra props (className,
 * data-* attributes) onto us — we forward them so focus styling still works.
 */
const MentionPill = React.forwardRef(function MentionPill(
  { trigger, value, data, className, ...rest },
  ref
) {
  // data.usernameColor is set when the mention is inserted via autocomplete.
  // Fall back to the theme accent if it's somehow absent (e.g. a node restored
  // without data).
  const color = (data && data.usernameColor) || 'var(--mention-color, #00d9ff)';
  return (
    <span
      ref={ref}
      className={['mention', className].filter(Boolean).join(' ')}
      style={{
        color,
        backgroundColor: 'var(--mention-bg, rgba(0, 217, 255, 0.14))',
        borderRadius: 4,
        padding: '0 3px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
      {...rest}
    >
      {trigger}
      {value}
    </span>
  );
});

// createBeautifulMentionNode returns [NodeClass, replacementConfig]. Both must
// be registered in the editor's `nodes` array so the library uses OUR pill
// instead of its default <span>.
export const [CCMentionNode, CCMentionNodeReplacement] =
  createBeautifulMentionNode(MentionPill);
